import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2, XCircle, PartyPopper } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { BASE_URL } from '../lib/api';

const API_URL = BASE_URL;

const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [result, setResult] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxAttempts = 10;

  const sessionId = searchParams.get('session_id');
  const isAddon = searchParams.get('addon');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
      setResult({ message: 'No session ID found' });
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setStatus('error');
      setResult({ message: 'Please log in to verify your payment' });
      return;
    }

    if (pollAttempts >= maxAttempts) {
      setStatus('error');
      setResult({ message: 'Payment verification timed out. Please contact support if you were charged.' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/subscription/checkout/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();

      if (data.status === 'paid' || data.payment_status === 'paid') {
        setStatus('success');
        setResult(data);
      } else if (data.status === 'expired') {
        setStatus('error');
        setResult({ message: 'Payment session expired. Please try again.' });
      } else {
        // Still pending, poll again
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPollAttempts(prev => prev + 1);
      
      if (pollAttempts < maxAttempts - 1) {
        setTimeout(pollPaymentStatus, 2000);
      } else {
        setStatus('error');
        setResult({ message: 'Error verifying payment. Please contact support.' });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-slate-800/50 border-slate-700">
        <CardHeader className="text-center">
          {status === 'checking' && (
            <>
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
              </div>
              <CardTitle className="text-white text-2xl">Verifying Payment</CardTitle>
              <CardDescription className="text-slate-400">
                Please wait while we confirm your payment...
              </CardDescription>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <PartyPopper className="h-8 w-8 text-green-500" />
              </div>
              <CardTitle className="text-white text-2xl">
                {isAddon ? 'Add-on Activated!' : 'Welcome to ' + (result?.plan_name || 'Your New Plan') + '!'}
              </CardTitle>
              <CardDescription className="text-slate-400">
                {result?.message || 'Your subscription is now active'}
              </CardDescription>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
              <CardTitle className="text-white text-2xl">Payment Issue</CardTitle>
              <CardDescription className="text-red-400">
                {result?.message || 'There was a problem with your payment'}
              </CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {status === 'success' && (
            <>
              {result?.plan && (
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-2">Your Plan Details</h4>
                  <ul className="text-sm text-slate-400 space-y-1">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500" />
                      Plan: {result.plan_name || result.plan}
                    </li>
                    {result.billing_cycle && (
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        Billing: {result.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {result?.addon && (
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-2">Add-on Activated</h4>
                  <p className="text-sm text-slate-400">
                    {result.addon === 'ugc_addon' 
                      ? 'UGC Ad Studio is now available in your account!' 
                      : result.message}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 hover:opacity-90"
                  onClick={() => navigate('/create')}
                  data-testid="start-creating-btn"
                >
                  Start Creating
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-white hover:bg-slate-700"
                  onClick={() => navigate('/library')}
                >
                  View Library
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <div className="flex gap-3">
              <Button
                className="flex-1 bg-slate-600 hover:bg-slate-500"
                onClick={() => navigate('/pricing')}
              >
                Back to Pricing
              </Button>
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-white hover:bg-slate-700"
                onClick={() => window.location.href = 'mailto:support@igoviralcreator.io'}
              >
                Contact Support
              </Button>
            </div>
          )}

          {status === 'checking' && (
            <div className="text-center text-slate-500 text-sm">
              Attempt {pollAttempts + 1} of {maxAttempts}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
